import React, { useCallback } from 'react'
import AsyncSelect from 'react-select/async'
import { api } from '../services/api'
import { useDebouncedCallback } from '../hooks'
import selectStyles from '../styles/react-select.module.scss'
import { Competitor } from '../types/forms'

// Option type extends Competitor with react-select required fields
interface CompetitorOption extends Competitor {
  value: string
  label: string
}

export interface CompetitorSelectProps {
  value: Competitor | Competitor[] | null
  onChange: (competitor: Competitor | Competitor[] | null) => void
  teamType?: 'singles' | 'doubles'
  isMulti?: boolean
  isClearable?: boolean
  placeholder?: string
  className?: string
  isDisabled?: boolean
  isModified?: boolean
}

// Convert Competitor to react-select option (exported for use in forms)
export const toOption = (c: Competitor): CompetitorOption => ({ ...c, value: c.tc_id, label: c.name })

const CompetitorSelect: React.FC<CompetitorSelectProps> = ({
  value,
  onChange,
  teamType = 'singles',
  isMulti = false,
  isClearable = true,
  placeholder = 'Search competitors...',
  className = '',
  isDisabled = false,
  isModified = false
}) => {
  const fetchCompetitors = useCallback(async (inputValue: string): Promise<CompetitorOption[]> => {
    if (inputValue.length < 2) return []

    try {
      const isTeam = teamType === 'doubles'
      const endpoint = isTeam ? '/teams' : '/players'
      const response = await api.get(endpoint, { name: inputValue, limit: 50 })
      const rawItems = isTeam ? response?.teams || [] : response?.players || []

      // Transform API response to Competitor format
      const competitors: Competitor[] = rawItems.map((item: any) => ({
        tc_id: isTeam ? item.tc_team_id : item.tc_competitor_id,
        ext_id: item.ext_competitor_id,
        name: item.tc_name || item.ext_name || 'Unknown',
        team_type: isTeam ? 'team' : (item.tc_team_type || item.ext_team_type)
      }))

      return competitors.map(toOption)
    } catch (error) {
      console.error(`Failed to load ${teamType === 'singles' ? 'players' : 'teams'}:`, error)
      return []
    }
  }, [teamType])

  const loadCompetitors = useDebouncedCallback(fetchCompetitors, 300)

  // Convert value to option format
  const selectValue = value
    ? (isMulti ? (value as Competitor[]).map(toOption) : toOption(value as Competitor))
    : null

  return (
    <div className={isModified ? selectStyles.modified : ''}>
      <AsyncSelect<CompetitorOption, typeof isMulti>
        value={selectValue}
        onChange={onChange as any}
        loadOptions={loadCompetitors}
        defaultOptions={false}
        openMenuOnFocus={false}
        openMenuOnClick={false}
        isMulti={isMulti}
        isClearable={isClearable}
        placeholder={placeholder}
        className={`${selectStyles.reactSelect} ${className}`}
        classNamePrefix="react-select"
        isDisabled={isDisabled}
        cacheOptions
        getOptionValue={(option) => option.tc_id}
        getOptionLabel={(option) => option.name}
        noOptionsMessage={({ inputValue }) =>
          inputValue.length < 2
            ? "Type at least 2 characters to search..."
            : `No ${teamType === 'singles' ? 'players' : 'teams'} found`
        }
      />
    </div>
  )
}

export default CompetitorSelect
